import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Container,
  Header,
  Table,
  Button,
  Icon,
  Message,
  Segment,
  Dimmer,
  Loader,
  Confirm,
  Input,
  Divider
} from 'semantic-ui-react';
import {
  fetchPathDefinitions,
  deletePathDefinition,
  createFullBackup,
  clearError,
  clearSuccess
} from '../../slices/pathRegistrySlice';

import { AuthStdFooter } from "../../utils/auth-std-footer";
import '../../styles/path-registry.css';

/**
 * PathDefinitionsList component for displaying and managing path definitions
 * @returns {JSX.Element} PathDefinitionsList component
 */
const PathDefinitionsList = () => {
  const dispatch = useDispatch();

  // Get path definitions data from Redux
  const { pathDefinitions, loading, error, success } = useSelector(
    (state) => state.user.pathRegistry
  );

  // Local state
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [backupConfirmOpen, setBackupConfirmOpen] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [localSuccess, setLocalSuccess] = useState(null);

  // Fetch path definitions on component mount
  useEffect(() => {
    dispatch(fetchPathDefinitions());
  }, [dispatch]);

  // Update local state when Redux state changes
  useEffect(() => {
    if (error) {
      setLocalError(error);
      setTimeout(() => {
        dispatch(clearError());
        setLocalError(null);
      }, 5000);
    }

    if (success) {
      setLocalSuccess(success);
      setTimeout(() => {
        dispatch(clearSuccess());
        setLocalSuccess(null);
      }, 5000);
    }
  }, [dispatch, error, success]);

  // Handle delete confirmation
  const handleDeleteClick = (name) => {
    setDeleteTarget(name);
    setConfirmOpen(true);
  };

  // Handle delete confirmation cancel
  const handleCancel = () => {
    setConfirmOpen(false);
    setDeleteTarget(null);
  };

  // Handle delete confirmation confirm
  const handleConfirm = async () => {
    if (deleteTarget) {
      try {
        await dispatch(deletePathDefinition(deleteTarget)).unwrap();
        setLocalSuccess(`Path definition "${deleteTarget}" deleted successfully`);
      } catch (err) {
        setLocalError(`Failed to delete path definition: ${err.message || 'Unknown error'}`);
      }
    }
    setConfirmOpen(false);
    setDeleteTarget(null);
  };

  // Handle backup button click
  const handleBackupClick = () => {
    setBackupConfirmOpen(true);
  };

  // Handle backup confirmation cancel
  const handleBackupCancel = () => {
    setBackupConfirmOpen(false);
  };

  // Handle backup confirmation confirm
  const handleBackupConfirm = async () => {
    setBackupConfirmOpen(false);
    try {
      await dispatch(createFullBackup()).unwrap();
      // Success message will be handled by Redux state
    } catch (err) {
      setLocalError(`Failed to create backup: ${err.message || 'Unknown error'}`);
    }
  };

  // Filter path definitions based on search term
  const filteredDefinitions = pathDefinitions.filter(def =>
    def.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    def.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="page-container" style={{ marginBottom: "70px" }}>
      <Container>
        <Segment>
          <Header as="h2">
            <Icon name="sitemap" />
            <Header.Content>
              Path Registry Management
              <Header.Subheader>Manage S3 path definitions</Header.Subheader>
            </Header.Content>
          </Header>
          <Divider />

          {/* Action buttons */}
          <div style={{ marginBottom: '1em', display: 'flex', justifyContent: 'space-between' }}>
            <Button
              as={Link}
              to="/admin/path-registry/create"
              primary
              icon
              labelPosition="left"
            >
              <Icon name="plus" />
              Create New Path Definition
            </Button>
            <div>
              <Button
                as={Link}
                to="/admin/path-registry/test"
                color="teal"
                icon
                labelPosition="left"
                style={{ marginRight: '0.5em' }}
              >
                <Icon name="lab" />
                Test Path Resolution
              </Button>
              <Button
                color="olive"
                icon
                labelPosition="left"
                onClick={handleBackupClick}
                disabled={loading}
              >
                <Icon name="cloud download" />
                Backup to S3
              </Button>
            </div>
          </div>

          {/* Search input */}
          <Input
            icon="search"
            placeholder="Search path definitions..."
            fluid
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginBottom: '1em' }}
          />

          {/* Messages */}
          {localSuccess && (
            <Message positive>
              <Message.Header>Success</Message.Header>
              <p>{localSuccess}</p>
            </Message>
          )}

          {localError && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{localError}</p>
            </Message>
          )}

          {/* Path definitions table */}
          {loading ? (
            <Segment>
              <Dimmer active inverted>
                <Loader inverted>Loading Path Definitions</Loader>
              </Dimmer>
              <div style={{ height: '200px' }}></div>
            </Segment>
          ) : pathDefinitions.length === 0 ? (
            <Message info>
              <Message.Header>No Path Definitions Found</Message.Header>
              <p>
                No path definitions have been created yet. Click the "Create New Path Definition" button to create one.
              </p>
            </Message>
          ) : filteredDefinitions.length === 0 ? (
            <Message warning>
              <Message.Header>No Matching Path Definitions</Message.Header>
              <p>
                No path definitions match your search criteria. Try adjusting your search term.
              </p>
            </Message>
          ) : (
            <Table celled striped>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Name</Table.HeaderCell>
                  <Table.HeaderCell>Description</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Required Segments</Table.HeaderCell>
                  <Table.HeaderCell>Parameters</Table.HeaderCell>
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredDefinitions.map((def) => (
                  <Table.Row key={def.name}>
                    <Table.Cell>
                      <strong>{def.name}</strong>
                      {def.uuid && <div><small>UUID: {def.uuid.substring(0, 8)}...</small></div>}
                    </Table.Cell>
                    <Table.Cell>{def.description}</Table.Cell>
                    <Table.Cell>
                      {def.active ? (
                        <span style={{ color: 'green' }}>
                          <Icon name="check circle" /> Active
                        </span>
                      ) : (
                        <span style={{ color: 'red' }}>
                          <Icon name="times circle" /> Inactive
                        </span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {def.pattern && def.pattern.requiredSegments ? (
                        <div style={{ maxWidth: '200px', overflowWrap: 'break-word' }}>
                          {def.pattern.requiredSegments.join(', ')}
                        </div>
                      ) : (
                        <em>None</em>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {def.params ? def.params.length : 0} parameters
                    </Table.Cell>
                    <Table.Cell>
                      <Button.Group size="small">
                        <Button
                          as={Link}
                          to={`/admin/path-registry/${def.name}/view`}
                          icon
                          labelPosition="left"
                          primary
                          size="small"
                        >
                          <Icon name="eye" />
                          View
                        </Button>
                        <Button
                          as={Link}
                          to={`/admin/path-registry/${def.name}/edit`}
                          icon
                          labelPosition="left"
                          color="yellow"
                          size="small"
                        >
                          <Icon name="edit" />
                          Edit
                        </Button>
                        <Button
                          icon
                          labelPosition="left"
                          color="red"
                          size="small"
                          onClick={() => handleDeleteClick(def.name)}
                        >
                          <Icon name="trash" />
                          Delete
                        </Button>
                      </Button.Group>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}

          {/* Delete confirmation dialog */}
          <Confirm
            open={confirmOpen}
            header="Delete Path Definition"
            content={`Are you sure you want to delete the path definition "${deleteTarget}"? This action cannot be undone.`}
            confirmButton="Delete"
            onCancel={handleCancel}
            onConfirm={handleConfirm}
          />

          {/* Backup confirmation dialog */}
          <Confirm
            open={backupConfirmOpen}
            className="path-registry-backup-confirm"
            header="Create Path Registry Backup"
            content={
              <div style={{ marginTop: "10px" }}>
                <p>Create a full backup of the Path Registry?</p>
                <p></p>
                <ol>
                  <span>This will:</span>
                  <li>Export all path definitions to a JSON file</li>
                  <li>Save locally to server/scripts/backups/</li>
                  <li>Upload the backup to S3 if configured</li>
                </ol>
              </div>
            }
            confirmButton="Create Backup"
            cancelButton="Cancel"
            onCancel={handleBackupCancel}
            onConfirm={handleBackupConfirm}
          />
        </Segment>
      </Container>
      {AuthStdFooter()}
    </div>
  );
};

export default PathDefinitionsList;
